import { useAppDispatch, useAppSelector } from '@/hooks';
import { setPasswordModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import React from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useChangePasswordMutation } from '@/store/api/userApi';
import { changePasswordSchema } from '@/utils/validation';
import { useUserInfo } from '@/hooks/useUserInfo';

type FormData = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordModal: React.FC = () => {
  const { passwordModalOpen: open } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const {username} = useUserInfo()

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onClose = () => {
    dispatch(setPasswordModalOpen(false));
    reset();
  };

  const onSubmit = async (data: FormData) => {
    try {
      await changePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }).unwrap();

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center pt-[50px] bg-black/70">
      {/* Modal */}
      <div className="w-[420px] rounded-lg overflow-hidden shadow-xl bg-[#1c1f2a] text-white h-fit">
        {/* Header */}
        <div className="flex items-center justify-between bg-yellow-400 px-4 py-3">
          <h2 className="text-black font-semibold text-lg">Change Password</h2>
          <button onClick={onClose}>
            <Icons.Cross className="text-black cursor-pointer" size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-3">
          <p className="text-sm text-gray-300">
            User name : <span className="font-semibold text-white">{username}</span>
          </p>

          <input
            type="password"
            placeholder="Enter Old Password"
            {...register('oldPassword')}
            className="w-full px-3 py-2 rounded bg-[#2a2e3d] text-sm outline-none"
          />
          {errors.oldPassword && (
            <p className="text-red-400 text-xs">{errors.oldPassword.message}</p>
          )}

          <input
            type="password"
            placeholder="Enter New Password"
            {...register('newPassword')}
            className="w-full px-3 py-2 rounded bg-[#2a2e3d] text-sm outline-none"
          />
          {errors.newPassword && (
            <p className="text-red-400 text-xs">{errors.newPassword.message}</p>
          )}

          <input
            type="password"
            placeholder="Re-Enter New Password"
            {...register('confirmPassword')}
            className="w-full px-3 py-2 rounded bg-[#2a2e3d] text-sm outline-none"
          />
          {errors.confirmPassword && (
            <p className="text-red-400 text-xs">
              {errors.confirmPassword.message}
            </p>
          )}

          {/* Buttons */}
          <div className="flex justify-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-green-500 px-4 py-1.5 rounded text-sm font-medium"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="bg-red-500 px-4 py-1.5 rounded text-sm font-medium"
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;